import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  ArrowLeft, Lock, Loader2, Calculator, BookOpen, PenLine, CheckCircle2,
  XCircle, Sparkles, FileText, ChevronRight, Trophy,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const DIFF_COLOR = (d) => d <= 3 ? 'bg-emerald-100 text-emerald-700' : d <= 6 ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700';

export default function SuttonStage2() {
  const navigate = useNavigate();
  const [locked, setLocked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [mocks, setMocks] = useState([]);
  const [active, setActive] = useState(null); // full mock doc

  useEffect(() => {
    axios.get(`${API}/sutton-stage2/list`, { headers: authHeader() })
      .then((r) => setMocks(r.data.mocks || []))
      .catch((e) => { if (e?.response?.status === 403) setLocked(true); })
      .finally(() => setLoading(false));
  }, []);

  const openMock = async (n) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/sutton-stage2/mock/${n}`, { headers: authHeader() });
      setActive(r.data);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || 'This mock is being prepared.');
    } finally { setLoading(false); }
  };

  if (loading && !active) return <Center><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></Center>;
  if (locked) return (
    <Center>
      <Card className="max-w-md border-slate-200"><CardContent className="p-8 text-center">
        <Lock className="h-10 w-10 mx-auto text-slate-400 mb-3" />
        <h1 className="text-xl font-bold text-slate-900 mb-2">Sutton Stage 2 Mock Series</h1>
        <p className="text-slate-600 mb-5">This mock series is for 11+ students.</p>
        <Button variant="outline" onClick={() => navigate('/welcome')} data-testid="s2-back"><ArrowLeft className="h-4 w-4 mr-2" />Back</Button>
      </CardContent></Card>
    </Center>
  );

  if (active) return <MockViewer mock={active} onBack={() => setActive(null)} navigate={navigate} />;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-white" data-testid="s2-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <button onClick={() => navigate('/welcome')} className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1 mb-4"><ArrowLeft className="h-4 w-4" />Dashboard</button>
        <h1 className="text-3xl font-bold text-slate-900">Sutton SET · Stage 2 Mock Series</h1>
        <p className="text-slate-600 mb-6">10 full mock papers — Maths (60) + English (Reading 30 + Writing 30) — from Foundation to Final Challenge.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          {mocks.map((m) => (
            <Card key={m.number}
              className={`border-slate-200 transition-all ${m.available ? 'cursor-pointer hover:shadow-md hover:-translate-y-0.5' : 'opacity-70'}`}
              onClick={() => m.available && openMock(m.number)} data-testid={`s2-mock-${m.number}`}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 text-white flex items-center justify-center font-bold shrink-0">{m.number}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-bold text-slate-900">Mock {m.number} · {m.name}</h3>
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${DIFF_COLOR(m.difficulty)}`}>Difficulty {m.difficulty}/10</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {m.available ? (m.last_maths_score != null ? `Last maths score: ${m.last_maths_score}` : 'Ready — Maths + English') : 'Coming soon'}
                  </p>
                </div>
                {m.available ? <ChevronRight className="h-5 w-5 text-slate-400" /> : <Lock className="h-4 w-4 text-slate-300" />}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

const Center = ({ children }) => (
  <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-white flex items-center justify-center p-6">{children}</div>
);

// --------------------------------------------------------------------------- viewer
function MockViewer({ mock, onBack, navigate }) {
  const [mathsAnswers, setMathsAnswers] = useState({});
  const [mathsResult, setMathsResult] = useState(null);
  const [marking, setMarking] = useState(false);
  const reading = mock.english?.reading || {};
  const writing = mock.english?.writing || {};

  const submitMaths = async () => {
    setMarking(true);
    try {
      const r = await axios.post(`${API}/sutton-stage2/submit-maths/${mock.number}`, { answers: mathsAnswers }, { headers: authHeader() });
      setMathsResult(r.data);
      toast.success(`Auto-marked: ${r.data.score}/${r.data.auto_markable}`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch { toast.error('Could not mark. Try again.'); }
    finally { setMarking(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-white" data-testid="s2-viewer">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <button onClick={onBack} className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1 mb-4"><ArrowLeft className="h-4 w-4" />All mocks</button>

        {/* Front page */}
        <Card className="border-slate-200 mb-6 overflow-hidden">
          <div className="bg-gradient-to-r from-indigo-600 to-violet-600 text-white p-6">
            <p className="text-indigo-100 text-sm">Sutton SET · Stage 2 Mock Series</p>
            <h1 className="text-2xl font-bold mt-1">Mock {mock.number} — {mock.name}</h1>
            <div className="flex gap-2 mt-3 flex-wrap text-xs">
              <Badge className="bg-white/20 border-0">Difficulty {mock.difficulty}/10</Badge>
              <Badge className="bg-white/20 border-0">Maths 60 marks · 60 min</Badge>
              <Badge className="bg-white/20 border-0">English 60 marks · 60 min</Badge>
            </div>
          </div>
          <CardContent className="p-5">
            <p className="text-sm text-slate-600">{mock.instructions}</p>
            {mock.skills_assessed?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-3">
                {mock.skills_assessed.map((s, i) => <span key={i} className="text-[11px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded">{s}</span>)}
              </div>
            )}
          </CardContent>
        </Card>

        <Tabs defaultValue="maths">
          <TabsList className="grid grid-cols-3 mb-4">
            <TabsTrigger value="maths" data-testid="s2-tab-maths"><Calculator className="h-4 w-4 mr-1" />Maths</TabsTrigger>
            <TabsTrigger value="reading" data-testid="s2-tab-reading"><BookOpen className="h-4 w-4 mr-1" />Reading</TabsTrigger>
            <TabsTrigger value="writing" data-testid="s2-tab-writing"><PenLine className="h-4 w-4 mr-1" />Writing</TabsTrigger>
          </TabsList>

          {/* MATHS */}
          <TabsContent value="maths" className="space-y-3">
            {mathsResult && (
              <Card className="border-emerald-200 bg-emerald-50" data-testid="s2-maths-result">
                <CardContent className="p-4 flex items-center gap-3">
                  <Trophy className="h-6 w-6 text-emerald-600" />
                  <div>
                    <p className="font-bold text-slate-900">Auto-marked: {mathsResult.score}/{mathsResult.auto_markable}</p>
                    <p className="text-xs text-slate-600">{mathsResult.challenge_marks} challenge marks + the writing task are marked by the Examinor.</p>
                  </div>
                </CardContent>
              </Card>
            )}
            {(mock.maths?.questions || []).map((q) => {
              const bd = mathsResult?.breakdown?.find((b) => b.no === q.no);
              return (
                <Card key={q.no} className="border-slate-200" data-testid={`s2-q-${q.no}`}>
                  <CardContent className="p-4">
                    <div className="flex justify-between gap-3">
                      <p className="text-sm text-slate-800"><b>Q{q.no}.</b> {q.question}</p>
                      <span className="text-xs text-slate-400 shrink-0">{q.marks} mark{q.marks > 1 ? 's' : ''} · {q.type === 'challenge' ? '⭐ Challenge' : q.topic}</span>
                    </div>
                    {q.type === 'mcq' && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {(q.options || []).map((o, i) => (
                          <button key={i} onClick={() => setMathsAnswers((a) => ({ ...a, [q.no]: o }))}
                            className={`text-xs px-2 py-1 rounded border ${mathsAnswers[q.no] === o ? 'bg-indigo-600 text-white border-indigo-600' : 'border-slate-200 text-slate-600'}`}>{o}</button>
                        ))}
                      </div>
                    )}
                    {q.type !== 'mcq' && (
                      <Input className="mt-2 max-w-xs" placeholder="Your answer" value={mathsAnswers[q.no] || ''}
                        onChange={(e) => setMathsAnswers((a) => ({ ...a, [q.no]: e.target.value }))} data-testid={`s2-ans-${q.no}`} />
                    )}
                    {bd && (
                      <div className="mt-2 text-xs">
                        {bd.type !== 'challenge' && (
                          <p className={bd.is_correct ? 'text-emerald-700' : 'text-rose-600'}>
                            {bd.is_correct ? <CheckCircle2 className="h-3.5 w-3.5 inline mr-1" /> : <XCircle className="h-3.5 w-3.5 inline mr-1" />}
                            Answer: {bd.correct_answer}
                          </p>
                        )}
                        <details className="mt-1"><summary className="cursor-pointer text-indigo-600">Worked solution & mark scheme</summary>
                          <p className="text-slate-600 mt-1 whitespace-pre-wrap">{bd.working}</p>
                          <p className="text-slate-500 mt-1"><b>Mark scheme:</b> {bd.mark_scheme}</p>
                        </details>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
            <Button onClick={submitMaths} disabled={marking} className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="s2-submit-maths">
              {marking ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Marking…</> : 'Submit maths for marking'}
            </Button>
          </TabsContent>

          {/* READING */}
          <TabsContent value="reading" className="space-y-4">
            {reading.passage && (
              <Card className="border-slate-200"><CardHeader className="pb-2">
                <CardTitle className="text-base text-slate-900">{reading.passage.title} <span className="text-xs font-normal text-slate-400">· {reading.passage.type}</span></CardTitle>
              </CardHeader><CardContent><p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{reading.passage.text}</p></CardContent></Card>
            )}
            {(reading.questions || []).map((q) => (
              <Card key={q.no} className="border-slate-200"><CardContent className="p-4">
                <div className="flex justify-between gap-3">
                  <p className="text-sm text-slate-800"><b>Q{q.no}.</b> {q.question}</p>
                  <span className="text-xs text-slate-400 shrink-0">{q.marks}m · {q.skill}</span>
                </div>
                <details className="mt-2 text-xs"><summary className="cursor-pointer text-indigo-600">Model answer & mark scheme</summary>
                  <p className="text-slate-600 mt-1">{q.answer}</p>
                  <p className="text-slate-500 mt-1"><b>Mark scheme:</b> {q.mark_scheme}</p>
                </details>
              </CardContent></Card>
            ))}
            {reading.vocabulary?.length > 0 && (
              <Card className="border-slate-200 bg-amber-50/40"><CardHeader className="pb-2"><CardTitle className="text-base text-slate-900">Vocabulary</CardTitle></CardHeader>
                <CardContent className="grid sm:grid-cols-2 gap-1.5 text-sm">
                  {reading.vocabulary.map((v, i) => <p key={i} className="text-slate-700"><b>{v.word}:</b> {v.meaning}</p>)}
                </CardContent></Card>
            )}
          </TabsContent>

          {/* WRITING */}
          <TabsContent value="writing" className="space-y-4">
            <Card className="border-slate-200"><CardHeader className="pb-2">
              <CardTitle className="text-base text-slate-900">Writing Task <Badge variant="outline" className="ml-1">{writing.task_type}</Badge></CardTitle>
            </CardHeader><CardContent>
              <p className="text-sm text-slate-800 font-medium">{writing.prompt}</p>
              {writing.tasks?.length > 1 && <p className="text-xs text-slate-500 mt-2">Alternative: {writing.tasks[1]}</p>}
              <Button className="mt-4 bg-emerald-600 hover:bg-emerald-700" onClick={() => navigate('/mark-my-work')} data-testid="s2-mark-writing">
                <Sparkles className="h-4 w-4 mr-2" />Write it, then mark it with Examinor
              </Button>
            </CardContent></Card>
            {writing.mark_scheme && (
              <Card className="border-slate-200"><CardHeader className="pb-2"><CardTitle className="text-base text-slate-900">Mark scheme</CardTitle></CardHeader>
                <CardContent><p className="text-sm text-slate-600 whitespace-pre-wrap">{writing.mark_scheme}</p></CardContent></Card>
            )}
            {writing.exemplars && (
              <div className="grid md:grid-cols-2 gap-4">
                <Card className="border-slate-200"><CardHeader className="pb-2"><CardTitle className="text-sm text-slate-700 flex items-center gap-1"><FileText className="h-4 w-4" />Expected standard</CardTitle></CardHeader>
                  <CardContent><p className="text-xs text-slate-600 whitespace-pre-wrap">{writing.exemplars.expected}</p></CardContent></Card>
                <Card className="border-emerald-200 bg-emerald-50/40"><CardHeader className="pb-2"><CardTitle className="text-sm text-emerald-700 flex items-center gap-1"><Trophy className="h-4 w-4" />Greater depth</CardTitle></CardHeader>
                  <CardContent><p className="text-xs text-slate-600 whitespace-pre-wrap">{writing.exemplars.greater_depth}</p></CardContent></Card>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
